import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  Download as DownloadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Search as SearchIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../api/axiosConfig";
import { endpoints } from "../../../api/endpoints";
import { getApiErrorMessage } from "../../../utils/apiError";
import { colors } from "../../../theme/colors";

type CertificateAvailability = "YES" | "NO";
type CertificateStatus =
  | "VALID"
  | "EXPIRING_SOON"
  | "EXPIRED"
  | "UPLOADED"
  | "NOT_UPLOADED";

interface VehicleRow {
  id: string;
  registrationNo: string;
  phoneNumber: string;
  createdAt: string;
  cylinderCount: number;
  metalPlateDetails: string;
  hasCertificate: CertificateAvailability;
  certificateStatus: CertificateStatus;
}

interface VehicleApiItem {
  registration_number?: string;
  phone?: string;
  created_at?: string;
  cylinder_count?: number;
  testing_verified?: boolean | null;
  certificate_status?: string;
  certificate_present?: boolean;
}

interface VehiclesApiResponse {
  items?: VehicleApiItem[];
  total?: number;
  page?: number;
  page_size?: number;
  pages?: number;
}

const PAGE_SIZE = 20;

const DEFAULT_STATUS_FILTER = "ALL";
const DEFAULT_CYLINDER_FILTER = "ALL";

const getValidPage = (value?: string | null) => {
  const parsedValue = Number(value);

  if (!Number.isInteger(parsedValue) || parsedValue < 1) {
    return 1;
  }

  return parsedValue;
};

const normalizeCertificateStatus = (value?: string): CertificateStatus => {
  switch ((value || "").toLowerCase().replace(/\s+/g, "_")) {
    case "valid":
      return "VALID";
    case "expired":
      return "EXPIRED";
    case "expiring_soon":
      return "EXPIRING_SOON";
    case "uploaded":
      return "UPLOADED";
    default:
      return "NOT_UPLOADED";
  }
};

const formatDate = (value?: string) => {
  if (!value) {
    return "--";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const getTodayDateInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getMetalPlateDetails = (value?: boolean | null) => {
  if (value === true) {
    return "Readable";
  }

  if (value === false) {
    return "Not Readable";
  }

  return "--";
};

const normalizeVehicle = (item: VehicleApiItem, index: number): VehicleRow => ({
  id: item.registration_number || `vehicle-${index + 1}`,
  registrationNo: item.registration_number || "--",
  phoneNumber: item.phone || "--",
  createdAt: formatDate(item.created_at),
  cylinderCount: item.cylinder_count || 0,
  metalPlateDetails: getMetalPlateDetails(item.testing_verified),
  hasCertificate: item.certificate_present ? "YES" : "NO",
  certificateStatus: normalizeCertificateStatus(item.certificate_status),
});

const getVehicleExportData = (rows: VehicleRow[]) =>
  rows.map((vehicle) => ({
    "Registration No": vehicle.registrationNo,
    "Phone Number": vehicle.phoneNumber,
    Date: vehicle.createdAt,
    "No. of Cylinders": vehicle.cylinderCount,
    "Metal Plate Details": vehicle.metalPlateDetails,
    "Has Certificate": vehicle.hasCertificate === "YES" ? "Yes" : "No",
    "Certificate Status":
      vehicle.certificateStatus === "VALID"
        ? "Valid"
        : vehicle.certificateStatus === "EXPIRING_SOON"
          ? "Expiring Soon"
          : vehicle.certificateStatus === "UPLOADED"
            ? "Uploaded"
            : vehicle.certificateStatus === "EXPIRED"
              ? "Expired"
              : "Not Uploaded",
  }));

const getVehicleFilterParams = ({
  statusFilter,
  cylinderFilter,
  dateFrom,
  dateTo,
  searchQuery,
}: {
  statusFilter: string;
  cylinderFilter: string;
  dateFrom: string;
  dateTo: string;
  searchQuery: string;
}) => ({
  certificate_status:
    statusFilter === "ALL"
      ? undefined
      : statusFilter === "NOT_UPLOADED" || statusFilter === "UPLOADED"
        ? undefined
        : statusFilter.toLowerCase(),
  certificate_present:
    statusFilter === "NOT_UPLOADED"
      ? false
      : statusFilter === "UPLOADED"
        ? true
        : undefined,
  cylinder_count: cylinderFilter === "ALL" ? undefined : cylinderFilter,
  start_date: dateFrom || undefined,
  end_date: dateTo || undefined,
  keyword: searchQuery.trim() || undefined,
});

const filterCardSx = {
  borderRadius: "14px",
  border: "1px solid #D1D5DB",
  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.14)",
  overflow: "hidden",
  bgcolor: "#FFFFFF",
};

const tableCardSx = {
  borderRadius: "10px",
  border: "1px solid #D9D9D9",
  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.08)",
  overflow: "hidden",
  bgcolor: "#FFFFFF",
};

const selectSx = {
  minWidth: 210,
  "& .MuiOutlinedInput-root": {
    height: 42,
    borderRadius: "6px",
    backgroundColor: "#FFFFFF",
    "& fieldset": {
      borderColor: "#D1D5DB",
    },
    "&:hover fieldset": {
      borderColor: "#C4CBD5",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: 1,
    },
  },
  "& .MuiSelect-select": {
    fontSize: "18px",
    color: "#4B5563",
    py: "0 !important",
    display: "flex",
    alignItems: "center",
  },
};

const searchInputSx = {
  width: 400,
  "& .MuiOutlinedInput-root": {
    height: 42,
    borderRadius: "6px",
    backgroundColor: "#FFFFFF",
    "& fieldset": {
      borderColor: "#D1D5DB",
    },
    "&:hover fieldset": {
      borderColor: "#C4CBD5",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "18px",
    color: "#4B5563",
    py: 0,
  },
};

const dateInputSx = {
  minWidth: 180,
  "& .MuiOutlinedInput-root": {
    height: 42,
    borderRadius: "6px",
    backgroundColor: "#FFFFFF",
    "& fieldset": {
      borderColor: "#D1D5DB",
    },
    "&:hover fieldset": {
      borderColor: "#C4CBD5",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    color: "#4B5563",
    py: 0,
  },
  "& .MuiInputLabel-root": {
    fontSize: "13px",
  },
};

const getCertificatePill = (hasCertificate: CertificateAvailability) => {
  const isAvailable = hasCertificate === "YES";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.7,
        px: 1.1,
        py: 0.55,
        borderRadius: "999px",
        bgcolor: isAvailable ? "#E8F3E8" : "#F8E1E5",
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          bgcolor: "#4B4B4B",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isAvailable ? (
          <CheckIcon sx={{ fontSize: 15, color: "#FFFFFF" }} />
        ) : (
          <WarningAmberIcon sx={{ fontSize: 15, color: "#FFFFFF" }} />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 500,
          color: isAvailable ? "#2E7D32" : "#E53935",
          lineHeight: 1,
        }}
      >
        {isAvailable ? "Yes" : "No"}
      </Typography>
    </Box>
  );
};

const getStatusPill = (status: CertificateStatus) => {
  if (status === "NOT_UPLOADED") {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 1.2,
          py: 0.55,
          borderRadius: "999px",
          bgcolor: "#F3F3F3",
        }}
      >
        <Typography sx={{ fontSize: "15px", color: "#666666", lineHeight: 1 }}>
          Not Uploaded
        </Typography>
      </Box>
    );
  }

  if (status === "UPLOADED") {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.7,
          px: 1.1,
          py: 0.55,
          borderRadius: "999px",
          bgcolor: "#E8F1FF",
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: "#4B4B4B",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckIcon sx={{ fontSize: 15, color: "#FFFFFF" }} />
        </Box>
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 500,
            color: "#2563EB",
            lineHeight: 1,
          }}
        >
          Uploaded
        </Typography>
      </Box>
    );
  }

  const isValid = status === "VALID";
  const isExpiring = status === "EXPIRING_SOON";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.7,
        px: 1.1,
        py: 0.55,
        borderRadius: "999px",
        bgcolor: isValid ? "#E8F3E8" : isExpiring ? "#FFF1E1" : "#FBE4E8",
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: isValid ? "50%" : 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isValid ? (
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              bgcolor: "#4B4B4B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckIcon sx={{ fontSize: 15, color: "#FFFFFF" }} />
          </Box>
        ) : (
          <WarningAmberIcon sx={{ fontSize: 22, color: "#4B4B4B" }} />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 500,
          color: isValid ? "#2E7D32" : isExpiring ? "#F97316" : "#E53935",
          lineHeight: 1,
        }}
      >
        {isValid ? "Valid" : isExpiring ? "Expiring Soon" : "Expired"}
      </Typography>
    </Box>
  );
};

const VehicleList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const maxDate = getTodayDateInputValue();
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isApplyingDateFilters, setIsApplyingDateFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const activeRequestIdRef = useRef(0);

  const searchQuery = searchParams.get("q") || "";
  const statusFilter = searchParams.get("status") || DEFAULT_STATUS_FILTER;
  const cylinderFilter =
    searchParams.get("cylinders") || DEFAULT_CYLINDER_FILTER;
  const dateFrom = searchParams.get("dateFrom") || "";
  const dateTo = searchParams.get("dateTo") || "";
  const page = getValidPage(searchParams.get("page"));
  const [draftDateFrom, setDraftDateFrom] = useState(dateFrom);
  const [draftDateTo, setDraftDateTo] = useState(dateTo);
  const hasPendingDateChanges =
    Boolean(draftDateFrom && draftDateTo) &&
    (draftDateFrom !== dateFrom || draftDateTo !== dateTo);

  const updateSearchParams = (
    updates: Partial<{
      q: string;
      status: string;
      cylinders: string;
      dateFrom: string;
      dateTo: string;
      page: number;
    }>,
  ) => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);

        if (Object.prototype.hasOwnProperty.call(updates, "q")) {
          const nextQuery = updates.q || "";

          if (nextQuery) {
            nextParams.set("q", nextQuery);
          } else {
            nextParams.delete("q");
          }
        }

        if (Object.prototype.hasOwnProperty.call(updates, "status")) {
          const nextStatus = updates.status || DEFAULT_STATUS_FILTER;

          if (nextStatus !== DEFAULT_STATUS_FILTER) {
            nextParams.set("status", nextStatus);
          } else {
            nextParams.delete("status");
          }
        }

        if (Object.prototype.hasOwnProperty.call(updates, "cylinders")) {
          const nextCylinderCount =
            updates.cylinders || DEFAULT_CYLINDER_FILTER;

          if (nextCylinderCount !== DEFAULT_CYLINDER_FILTER) {
            nextParams.set("cylinders", nextCylinderCount);
          } else {
            nextParams.delete("cylinders");
          }
        }

        if (Object.prototype.hasOwnProperty.call(updates, "dateFrom")) {
          const nextDateFrom = updates.dateFrom || "";

          if (nextDateFrom) {
            nextParams.set("dateFrom", nextDateFrom);
          } else {
            nextParams.delete("dateFrom");
          }
        }

        if (Object.prototype.hasOwnProperty.call(updates, "dateTo")) {
          const nextDateTo = updates.dateTo || "";

          if (nextDateTo) {
            nextParams.set("dateTo", nextDateTo);
          } else {
            nextParams.delete("dateTo");
          }
        }

        if (Object.prototype.hasOwnProperty.call(updates, "page")) {
          const nextPage = updates.page || 1;

          if (nextPage > 1) {
            nextParams.set("page", String(nextPage));
          } else {
            nextParams.delete("page");
          }
        }

        return nextParams;
      },
      { replace: true },
    );
  };

  const applyDateFilters = () => {
    setIsApplyingDateFilters(true);
    updateSearchParams({
      dateFrom: draftDateFrom,
      dateTo: draftDateTo,
      page: 1,
    });
  };

  useEffect(() => {
    setDraftDateFrom(dateFrom);
    setDraftDateTo(dateTo);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = activeRequestIdRef.current + 1;

    activeRequestIdRef.current = requestId;

    const fetchVehicles = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await api.get<VehiclesApiResponse>(
          endpoints.dashboard.vehicles,
          {
            params: {
              ...getVehicleFilterParams({
                statusFilter,
                cylinderFilter,
                dateFrom,
                dateTo,
                searchQuery,
              }),
              page,
              page_size: PAGE_SIZE,
            },
            signal: controller.signal,
          },
        );

        const items = Array.isArray(response.data?.items)
          ? response.data.items
          : [];

        setVehicles(items.map((item, index) => normalizeVehicle(item, index)));
        setTotalCount(response.data?.total || items.length);
        setTotalPages(
          Math.max(
            1,
            response.data?.pages ||
              Math.ceil((response.data?.total || items.length) / PAGE_SIZE),
          ),
        );
      } catch (error: any) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        setErrorMessage(
          getApiErrorMessage(
            error,
            "Failed to load registered vehicles. Please try again.",
          ),
        );
        setVehicles([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        if (activeRequestIdRef.current === requestId) {
          setIsLoading(false);
          setIsApplyingDateFilters(false);
        }
      }
    };

    fetchVehicles();

    return () => {
      controller.abort();
    };
  }, [cylinderFilter, dateFrom, dateTo, page, searchQuery, statusFilter]);

  const filteredVehicles = useMemo(() => vehicles, [vehicles]);

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const response = await api.get<VehiclesApiResponse>(
        endpoints.dashboard.vehiclesExport,
        {
          params: getVehicleFilterParams({
            statusFilter,
            cylinderFilter,
            dateFrom,
            dateTo,
            searchQuery,
          }),
        },
      );

      const items = Array.isArray(response.data?.items)
        ? response.data.items
        : [];
      const exportRows = items.map((item, index) =>
        normalizeVehicle(item, index),
      );
      const exportData = getVehicleExportData(exportRows);

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      worksheet["!cols"] = [
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 18 },
        { wch: 20 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Registered Vehicles");
      XLSX.writeFile(workbook, "Registered_Vehicles.xlsx");
    } catch (error: any) {
      setErrorMessage(
        getApiErrorMessage(
          error,
          "Failed to export registered vehicles. Please try again.",
        ),
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: 3,
        bgcolor: "#F5F5F5",
        minHeight: "100vh",
      }}
    >
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...filterCardSx, mb: 3.2 }}>
        <Box sx={{ px: 3, py: 3.2 }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#232323",
              mb: 2.8,
            }}
          >
            Filter & Search
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              // flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <TextField
                placeholder="Search by registration or phone..."
                value={searchQuery}
                onChange={(event) =>
                  updateSearchParams({ q: event.target.value, page: 1 })
                }
                sx={searchInputSx}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon sx={{ color: "#666666", fontSize: 31 }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <FormControl size="small" sx={selectSx}>
                <Select
                  value={statusFilter}
                  onChange={(event: SelectChangeEvent) =>
                    updateSearchParams({
                      status: event.target.value,
                      page: 1,
                    })
                  }
                  displayEmpty
                  IconComponent={KeyboardArrowDownIcon}
                  renderValue={(selected) => {
                    if (selected === "ALL") {
                      return "All Status";
                    }

                    if (selected === "VALID") {
                      return "Valid";
                    }

                    if (selected === "EXPIRING_SOON") {
                      return "Expiring Soon";
                    }

                    if (selected === "UPLOADED") {
                      return "Uploaded";
                    }

                    if (selected === "EXPIRED") {
                      return "Expired";
                    }

                    return "Not Uploaded";
                  }}
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="VALID">Valid</MenuItem>
                  <MenuItem value="EXPIRING_SOON">Expiring Soon</MenuItem>
                  <MenuItem value="UPLOADED">Uploaded</MenuItem>
                  <MenuItem value="EXPIRED">Expired</MenuItem>
                  <MenuItem value="NOT_UPLOADED">Not Uploaded</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={selectSx}>
                <Select
                  value={cylinderFilter}
                  onChange={(event: SelectChangeEvent) =>
                    updateSearchParams({
                      cylinders: event.target.value,
                      page: 1,
                    })
                  }
                  displayEmpty
                  IconComponent={KeyboardArrowDownIcon}
                  renderValue={(selected) =>
                    selected === "ALL" ? "No of Cylinders" : `${selected}`
                  }
                >
                  <MenuItem value="ALL">No of Cylinders</MenuItem>
                  <MenuItem value="1">1</MenuItem>
                  <MenuItem value="2">2</MenuItem>
                  <MenuItem value="3">3</MenuItem>
                  <MenuItem value="4">4</MenuItem>
                  <MenuItem value="5">5</MenuItem>
                  <MenuItem value="6">6</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={draftDateFrom}
                onChange={(event) => setDraftDateFrom(event.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { "aria-label": "Start date", max: maxDate },
                }}
                sx={dateInputSx}
              />

              <TextField
                size="small"
                type="date"
                label="End Date"
                value={draftDateTo}
                onChange={(event) => setDraftDateTo(event.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { "aria-label": "End date", max: maxDate },
                }}
                sx={dateInputSx}
              />

              <Button
                variant="contained"
                startIcon={
                  isApplyingDateFilters ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SearchIcon />
                  )
                }
                onClick={applyDateFilters}
                disabled={isApplyingDateFilters || !hasPendingDateChanges}
                sx={{
                  height: 42,
                  px: 2,
                  borderRadius: "8px",
                  textTransform: "none",
                  bgcolor: colors.primary,
                  fontSize: "14px",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: colors.primary,
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#D1D5DB",
                    color: "#6B7280",
                  },
                }}
              >
                {isApplyingDateFilters ? "Applying..." : "Apply Dates"}
              </Button>
            </Box>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={isExporting}
              sx={{
                height: 42,
                px: 2.4,
                borderRadius: "4px",
                textTransform: "none",
                borderColor: "#66BB6A",
                color: "#2E7D32",
                fontSize: "16px",
                fontWeight: 500,
                "&:hover": {
                  borderColor: "#2E7D32",
                  bgcolor: "#F3FBF3",
                },
              }}
            >
              {isExporting ? "Exporting..." : "Export"}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={tableCardSx}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: "#F7F7F7" }}>
              <TableRow>
                {[
                  "Registration No",
                  "Phone Number",
                  "Date",
                  "No. of Cylinders",
                  "Metal Plate Details",
                  "Has Certificate",
                  "Certificate Status",
                  "Actions",
                ].map((heading) => (
                  <TableCell
                    key={heading}
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      py: 2.3,
                      borderBottom: "1px solid #E1E1E1",
                    }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box
                      sx={{ py: 7, display: "flex", justifyContent: "center" }}
                    >
                      <CircularProgress size={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredVehicles.map((vehicle) => (
                  <TableRow
                    key={vehicle.id}
                    sx={{
                      "& .MuiTableCell-root": {
                        borderBottom: "1px solid #E5E5E5",
                        py: 2.45,
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: colors.primary,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {vehicle.registrationNo}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: colors.black,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {vehicle.phoneNumber}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: colors.black,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {vehicle.createdAt}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: colors.black,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {vehicle.cylinderCount}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: colors.black,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {vehicle.metalPlateDetails}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {getCertificatePill(vehicle.hasCertificate)}
                    </TableCell>

                    <TableCell>
                      {getStatusPill(vehicle.certificateStatus)}
                    </TableCell>

                    <TableCell>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          navigate(
                            `/dashboard/registered-vehicles/${encodeURIComponent(vehicle.registrationNo)}`,
                          )
                        }
                        sx={{
                          minWidth: 160,
                          height: 38,
                          borderRadius: "4px",
                          textTransform: "none",
                          borderColor: "#81C784",
                          color: "#2E7D32",
                          fontSize: "16px",
                          fontWeight: 500,
                          "&:hover": {
                            borderColor: "#2E7D32",
                            bgcolor: "#F3FBF3",
                          },
                        }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}

              {!isLoading && filteredVehicles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <Typography sx={{ color: "#666666", fontSize: "15px" }}>
                        No registered vehicles found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && totalPages > 1 && (
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              borderTop: "1px solid #E5E5E5",
            }}
          >
            <Typography sx={{ color: "#666666", fontSize: "14px" }}>
              Showing {filteredVehicles.length} of {totalCount} vehicles
            </Typography>
            <Pagination
              count={Math.max(1, totalPages)}
              page={page}
              onChange={(_, value) => updateSearchParams({ page: value })}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default VehicleList;
